/**
 * Time Trial Recording Modal Component
 * 
 * Modal dialog for recording time trial results
 * - Form with rider, time (MM:SS), trail name, direction, notes
 * - Form validation
 * - API integration
 * - Success/error handling
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7
 */

import { useState, useCallback, useEffect } from 'react';
import { restClient, Rider, TrailConfiguration, Event } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { useUIStore } from '../../../store/uiStore';
import {
  validateTimeTrialForm,
  hasValidationErrors,
  TimeTrialFormData,
  TimeTrialValidationErrors,
  DIRECTION_OPTIONS
} from '../../../utils/timeTrials';
import { formatRiderName } from '../../../utils/riderDisplay';

interface TimeTrialRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName?: string;
  event?: Event; // Full event object for trail configuration
  defaultTrailName?: string;
  defaultDirection?: string;
  onSuccess: () => void;
  editMode?: boolean;
  existingTrial?: {
    rider_id: string;
    time: number;
    trail_name: string;
    direction: string;
    notes?: string;
  };
}

export function TimeTrialRecordModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  event,
  defaultTrailName = 'Dwelling',
  defaultDirection = 'Clockwise',
  onSuccess,
  editMode = false,
  existingTrial
}: TimeTrialRecordModalProps) {
  const showToast = useUIStore(state => state.showToast);
  const [formData, setFormData] = useState<TimeTrialFormData>({
    rider_id: '',
    minutes: '',
    seconds: '',
    trail_name: defaultTrailName,
    direction: defaultDirection,
    notes: ''
  });

  const [errors, setErrors] = useState<TimeTrialValidationErrors>({});
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [trailConfigurations, setTrailConfigurations] = useState<TrailConfiguration[]>([]);
  const [loadingTrails, setLoadingTrails] = useState(true);
  const [selectedTrailName, setSelectedTrailName] = useState<string>('');
  const [availableDirections, setAvailableDirections] = useState<string[]>([]);

  /**
   * Fetch riders for dropdown
   */
  const fetchRiders = useCallback(async () => {
    try {
      setLoadingRiders(true);
      const ridersList = await restClient.getRiders();
      // Filter to active riders only
      const activeRiders = ridersList.filter(r => r.status === 'active' || !r.status);
      // Sort by name
      activeRiders.sort((a, b) => a.name.localeCompare(b.name));
      setRiders(activeRiders);
    } catch (err) {
      console.error('Failed to fetch riders:', err);
      showToast('error', 'Failed to load riders list');
    } finally {
      setLoadingRiders(false);
    }
  }, [showToast]);

  /**
   * Fetch trail configurations for dropdowns
   */
  const fetchTrailConfigurations = useCallback(async () => {
    try {
      setLoadingTrails(true);
      const trails = await restClient.getTrailConfigurations();
      setTrailConfigurations(trails);
      
      // Priority 1: Pre-populate from event trail configuration (if not in edit mode)
      if (event?.trail_configuration_id && !editMode) {
        const eventTrailConfig = trails.find(t => t.trail_id === event.trail_configuration_id);
        if (eventTrailConfig) {
          setSelectedTrailName(eventTrailConfig.trail_name);
          setFormData(prev => ({
            ...prev,
            trail_name: eventTrailConfig.trail_name,
            direction: eventTrailConfig.direction
          }));
          // Set available directions for event trail
          const directions = trails
            .filter(t => t.trail_name === eventTrailConfig.trail_name)
            .map(t => t.direction);
          setAvailableDirections(directions);
          return; // Exit early - event trail config takes priority
        }
      }
      
      // Priority 2: Find default trail if exists (and not in edit mode)
      const defaultTrail = trails.find(t => t.is_default);
      if (defaultTrail && !editMode) {
        setSelectedTrailName(defaultTrail.trail_name);
        setFormData(prev => ({
          ...prev,
          trail_name: defaultTrail.trail_name,
          direction: defaultTrail.direction
        }));
        // Set available directions for default trail
        const directions = trails
          .filter(t => t.trail_name === defaultTrail.trail_name)
          .map(t => t.direction);
        setAvailableDirections(directions);
      }
    } catch (err) {
      console.error('Failed to fetch trail configurations:', err);
      showToast('error', 'Failed to load trail configurations');
    } finally {
      setLoadingTrails(false);
    }
  }, [showToast, editMode, event]);

  /**
   * Get available directions for selected trail
   */
  const getAvailableDirections = useCallback((trailName: string): string[] => {
    return trailConfigurations
      .filter(t => t.trail_name === trailName)
      .map(t => t.direction);
  }, [trailConfigurations]);

  /**
   * Handle trail name selection
   */
  const handleTrailChange = useCallback((trailName: string) => {
    setSelectedTrailName(trailName);
    const directions = getAvailableDirections(trailName);
    setAvailableDirections(directions);
    
    // Auto-select direction if only one available
    if (directions.length === 1) {
      setFormData(prev => ({ ...prev, trail_name: trailName, direction: directions[0] }));
    } else {
      setFormData(prev => ({ ...prev, trail_name: trailName, direction: '' }));
    }
    
    // Clear direction error
    if (errors.direction) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.direction;
        return newErrors;
      });
    }
  }, [getAvailableDirections, errors.direction]);

  /**
   * Reset form when modal opens/closes
   */
  useEffect(() => {
    if (isOpen) {
      if (editMode && existingTrial) {
        // Pre-fill form with existing trial data
        const minutes = Math.floor(existingTrial.time / 60);
        const seconds = existingTrial.time % 60;
        setFormData({
          rider_id: existingTrial.rider_id,
          minutes: minutes.toString(),
          seconds: seconds.toString().padStart(2, '0'),
          trail_name: existingTrial.trail_name,
          direction: existingTrial.direction,
          notes: existingTrial.notes || ''
        });
        setSelectedTrailName(existingTrial.trail_name);
      } else {
        // Reset form for new entry
        setFormData({
          rider_id: '',
          minutes: '',
          seconds: '',
          trail_name: defaultTrailName,
          direction: defaultDirection,
          notes: ''
        });
        setSelectedTrailName('');
      }
      setErrors({});
      fetchRiders();
      fetchTrailConfigurations();
    }
  }, [isOpen, defaultTrailName, defaultDirection, editMode, existingTrial, fetchRiders, fetchTrailConfigurations]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback((
    field: keyof TimeTrialFormData,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateTimeTrialForm(formData);
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      showToast('error', 'Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      // Convert time to seconds
      const totalSeconds = parseInt(formData.minutes) * 60 + parseInt(formData.seconds);

      if (editMode && existingTrial) {
        // Update existing time trial
        await restClient.updateTimeTrial({
          event_id: eventId,
          rider_id: existingTrial.rider_id,
          recorded_time: totalSeconds,
          trail_name: formData.trail_name,
          direction: formData.direction,
          notes: formData.notes || undefined
        });
        showToast('success', 'Time trial updated successfully');
      } else {
        // Create new time trial
        await restClient.createTimeTrial({
          event_id: eventId,
          rider_id: formData.rider_id,
          recorded_time: totalSeconds,
          trail_name: formData.trail_name,
          direction: formData.direction,
          notes: formData.notes || undefined
        });
        showToast('success', 'Time trial recorded successfully');
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save time trial:', err);
      showToast('error', `Failed to ${editMode ? 'update' : 'record'} time trial. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [formData, eventId, editMode, existingTrial, onSuccess, onClose, showToast]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancel}></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900" id="modal-title">
                {editMode ? 'Edit Time Trial' : 'Record Time Trial'}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {eventName && (
              <p className="text-sm text-gray-600 mt-1">Event: {eventName}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Rider Selection */}
            <div>
              <label htmlFor="rider_id" className="block text-sm font-medium text-gray-700 mb-1">
                Rider <span className="text-red-500">*</span>
              </label>
              {loadingRiders ? (
                <p className="text-sm text-gray-500">Loading riders...</p>
              ) : (
                <select
                  id="rider_id"
                  value={formData.rider_id}
                  onChange={(e) => handleChange('rider_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.rider_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || editMode}
                >
                  <option value="">Select Rider...</option>
                  {riders.map((rider) => (
                    <option key={rider.rider_id} value={rider.rider_id}>
                      {formatRiderName(rider)}
                    </option>
                  ))}
                </select>
              )}
              {errors.rider_id && (
                <p className="mt-1 text-sm text-red-600">{errors.rider_id}</p>
              )}
            </div>

            {/* Time Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    placeholder="MM"
                    value={formData.minutes}
                    onChange={(e) => handleChange('minutes', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.minutes ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {errors.minutes && (
                    <p className="mt-1 text-sm text-red-600">{errors.minutes}</p>
                  )}
                </div>
                <span className="text-gray-500 font-semibold">:</span>
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    placeholder="SS"
                    value={formData.seconds}
                    onChange={(e) => handleChange('seconds', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.seconds ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {errors.seconds && (
                    <p className="mt-1 text-sm text-red-600">{errors.seconds}</p>
                  )}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">Format: MM:SS</p>
            </div>

            {/* Trail Name */}
            <div>
              <label htmlFor="trail_name" className="block text-sm font-medium text-gray-700 mb-1">
                Trail Name <span className="text-red-500">*</span>
              </label>
              {loadingTrails ? (
                <p className="text-sm text-gray-500">Loading trails...</p>
              ) : trailConfigurations.length === 0 ? (
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    No trail configurations available. Please add trails in{' '}
                    <a href="/admin/configuration" className="text-primary-600 hover:text-primary-700 underline">
                      Configuration
                    </a>.
                  </p>
                  <input
                    type="text"
                    id="trail_name"
                    value={formData.trail_name}
                    onChange={(e) => handleChange('trail_name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.trail_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Dwelling"
                    maxLength={100}
                    disabled={loading}
                  />
                </div>
              ) : (
                <select
                  id="trail_name"
                  value={selectedTrailName}
                  onChange={(e) => handleTrailChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.trail_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                >
                  <option value="">Select Trail...</option>
                  {Array.from(new Set(trailConfigurations.map(t => t.trail_name))).map((trailName) => (
                    <option key={trailName} value={trailName}>
                      {trailName}
                    </option>
                  ))}
                </select>
              )}
              {errors.trail_name && (
                <p className="mt-1 text-sm text-red-600">{errors.trail_name}</p>
              )}
            </div>

            {/* Direction */}
            <div>
              <label htmlFor="direction" className="block text-sm font-medium text-gray-700 mb-1">
                Direction <span className="text-red-500">*</span>
              </label>
              {loadingTrails ? (
                <p className="text-sm text-gray-500">Loading directions...</p>
              ) : trailConfigurations.length === 0 ? (
                <select
                  id="direction"
                  value={formData.direction}
                  onChange={(e) => handleChange('direction', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.direction ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                >
                  <option value="">Select Direction...</option>
                  {DIRECTION_OPTIONS.map((dir) => (
                    <option key={dir} value={dir}>
                      {dir}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="direction"
                  value={formData.direction}
                  onChange={(e) => handleChange('direction', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.direction ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading || !selectedTrailName}
                >
                  <option value="">Select Direction...</option>
                  {availableDirections.map((dir) => (
                    <option key={dir} value={dir}>
                      {dir}
                    </option>
                  ))}
                </select>
              )}
              {errors.direction && (
                <p className="mt-1 text-sm text-red-600">{errors.direction}</p>
              )}
              {selectedTrailName && availableDirections.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">No directions available for this trail</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.notes ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Strong headwind on back stretch"
                rows={3}
                maxLength={500}
                disabled={loading}
              />
              {errors.notes && (
                <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {formData.notes.length}/500 characters
              </p>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
            <Button
              onClick={handleCancel}
              variant="secondary"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || loadingRiders}
            >
              {loading ? (editMode ? 'Updating...' : 'Recording...') : (editMode ? 'Update Time' : 'Record Time')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
